import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Share,
  Eye,
  Settings,
  ChevronRight,
  ChevronDown,
  Calendar,
  Camera,
  Sparkles,
  Users,
  PartyPopper,
  CheckCircle2,
  X,
  Edit3,
  QrCode,
  Wallet,
  Building,
  Plus,
  TestTube,
  Rocket,
  Clock,
  Scissors,
  Palette,
  Heart,
  Plane,
  ShoppingBag,
  Leaf,
  GraduationCap,
  UtensilsCrossed,
  Trash2,
  Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { DefaultOrgIcon } from "@/components/DefaultAvatars";
import { getCategoryPreset, CATEGORY_PRESETS } from "@/lib/categoryConfig";
import PILOT_STAGE_COPY from "@/lib/pilotStageCopy";
import AddDashboardModal from "@/components/profile/AddDashboardModal";
import HostedBrandsSection from "@/components/HostedBrandsSection";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";

// Canonical category color + icon resolver (R9/R11/R26).
// Reads from /lib/categoryConfig.js (which mirrors `categories` collection).
// Accepts ids like "makeup", "cat_makeup", "nail" -> always resolves to canonical.
const resolveCategory = (rawId) => {
  if (!rawId) return null;
  const normalized = String(rawId).toLowerCase().replace(/^cat_/, "").trim();
  return getCategoryPreset(normalized);
};
const getCatColor = (rawId, fallback = "#6B7280") => resolveCategory(rawId)?.iconColor || fallback;
const getCatBg = (rawId, fallback = "#F3F4F6") => resolveCategory(rawId)?.bgColor || fallback;
const getCatIcon = (rawId, fallback = Calendar) => resolveCategory(rawId)?.icon || fallback;

// Brand colors for card
const BRAND_COLORS = [
  { id: "black", hex: "#000000", name: "Black" },
  { id: "blue", hex: "#2563eb", name: "Blue" },
  { id: "pink", hex: "#db2777", name: "Pink" },
  { id: "purple", hex: "#9333ea", name: "Purple" },
  { id: "green", hex: "#27803d", name: "Green" },
  { id: "teal", hex: "#2e9488", name: "Teal" },
];

export const WalletCardSettings = ({ 
  cardSettings, 
  setCardSettings, 
  onSave,
  user,
  walletColor,
  onColorChange
}) => {
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(cardSettings);
      toast.success("Card settings saved!");
    } catch (err) {
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };
  
  return (
    <div className="space-y-4 p-4 bg-white/90 backdrop-blur rounded-xl border border-white/20">
      <h3 className="font-semibold text-sm flex items-center gap-2 text-gray-800">
        <Settings className="w-4 h-4" />
        Card Display Settings
      </h3>
      
      {/* Card Color Section */}
      {onColorChange && (
        <div>
          <p className="text-sm font-medium text-gray-800 mb-2">Card Color</p>
          <div className="flex gap-2">
            {BRAND_COLORS.map((color) => (
              <button
                key={color.id}
                onClick={() => onColorChange(color.hex)}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  walletColor === color.hex 
                    ? 'border-gray-400 scale-110 ring-2 ring-offset-1 ring-gray-300' 
                    : 'border-gray-200 hover:scale-105'
                }`}
                style={{ backgroundColor: color.hex }}
                title={color.name}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Toggle: Show Profile Photo */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">Show Photo</p>
          <p className="text-xs text-gray-500">Display profile photo on card</p>
        </div>
        <Switch 
          checked={cardSettings.show_photo}
          onCheckedChange={(checked) => setCardSettings({...cardSettings, show_photo: checked})}
        />
      </div>
      
      {/* Toggle: Show Full Name vs Nickname */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">Show Full Name</p>
          <p className="text-xs text-gray-500">Use nickname if disabled</p>
        </div>
        <Switch 
          checked={cardSettings.show_full_name}
          onCheckedChange={(checked) => setCardSettings({...cardSettings, show_full_name: checked})}
        />
      </div>
      
      {!cardSettings.show_full_name && (
        <div>
          <label className="text-xs text-gray-600">Nickname</label>
          <Input 
            value={cardSettings.nickname || ""}
            onChange={(e) => setCardSettings({...cardSettings, nickname: e.target.value})}
            placeholder="Enter nickname"
            className="mt-1 bg-white text-gray-900"
          />
        </div>
      )}
      
      {/* Toggle: Show Title */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">Show Title</p>
          <p className="text-xs text-gray-500">Display role/title on card</p>
        </div>
        <Switch 
          checked={cardSettings.show_title}
          onCheckedChange={(checked) => setCardSettings({...cardSettings, show_title: checked})}
        />
      </div>
      
      {/* Toggle: Show Social Media */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">Show Social Media</p>
          <p className="text-xs text-gray-500">Display social handles</p>
        </div>
        <Switch 
          checked={cardSettings.show_social_media}
          onCheckedChange={(checked) => setCardSettings({...cardSettings, show_social_media: checked})}
        />
      </div>
      
      {/* Toggle: Accessibility/Invisible Disabilities */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">Accessibility Info</p>
          <p className="text-xs text-gray-500">Share accessibility needs</p>
        </div>
        <Switch 
          checked={cardSettings.show_accessibility}
          onCheckedChange={(checked) => setCardSettings({...cardSettings, show_accessibility: checked})}
        />
      </div>
      
      {cardSettings.show_accessibility && (
        <div>
          <label className="text-xs text-gray-600">Accessibility Notes</label>
          <Input 
            value={cardSettings.accessibility_notes || ""}
            onChange={(e) => setCardSettings({...cardSettings, accessibility_notes: e.target.value})}
            placeholder="e.g., Hearing impaired, mobility assistance"
            className="mt-1 bg-white text-gray-900"
          />
        </div>
      )}
      
      <Button onClick={handleSave} disabled={saving} className="w-full bg-gray-900 text-white hover:bg-gray-800">
        {saving ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
};

export const UpcomingEventsSection = ({ 
  hostedEvents = [], 
  attendingEvents = [],
  onViewEvent,
  userId
}) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const navigate = useNavigate();
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loadingAppts, setLoadingAppts] = useState(true);

  // Fetch real upcoming appointments from canonical data
  useEffect(() => {
    const fetchUpcoming = async () => {
      try {
        const res = await axios.get(`${API}/canonical/dashboard/${userId || 'me'}`, { withCredentials: true });
        setUpcomingAppointments(res.data?.upcoming || []);
      } catch (e) {
        // Fallback: no canonical data yet
        setUpcomingAppointments([]);
      }
      setLoadingAppts(false);
    };
    if (userId) fetchUpcoming();
    else setLoadingAppts(false);
  }, [userId]);

  // Combine: real appointments + hosted/attending events
  const allEvents = [...hostedEvents, ...attendingEvents.filter(e => 
    !hostedEvents.some(h => h.event_id === e.event_id)
  )];
  
  const hasContent = upcomingAppointments.length > 0 || allEvents.length > 0;

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http') || path.startsWith('data:')) return path;
    return `${BASE_URL}${path}`;
  };

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-sm text-gray-700">Upcoming</h3>
      
      {/* Real upcoming appointments */}
      {upcomingAppointments.map((appt) => {
        const catId = appt.provider?.primary_category_id || '';
        const catColor = getCatColor(catId);
        const CatIcon = getCatIcon(catId);
        const apptDate = appt.appointment_date ? new Date(appt.appointment_date) : null;
        return (
          <Card key={appt.provider_appointment_id} className="border-gray-200" data-testid={`upcoming-appt-${appt.provider_appointment_id}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${catColor}20` }}
              >
                <CatIcon className="w-5 h-5" style={{ color: catColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm truncate">{appt.service_name}</h4>
                <p className="text-xs text-gray-500">
                  {appt.provider?.display_name || appt.client_name || 'Provider'}
                  {apptDate ? ` - ${apptDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                </p>
              </div>
              <Button size="sm" variant="ghost">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        );
      })}

      {/* Hosted/attending events */}
      {allEvents.slice(0, 3).map((event) => {
        const isHost = hostedEvents.some(h => h.event_id === event.event_id);
        const catColor = getCatColor(event.pilot_category);
        const CatIcon = getCatIcon(event.pilot_category);
        const coverUrl = getImageUrl(event.cover_image) || getImageUrl(event.logo_image) || getImageUrl(event.image);
        return (
          <Card key={event.event_id} className="border-gray-200">
            <CardContent className="p-3 flex items-center gap-3">
              {coverUrl ? (
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                  <img src={coverUrl} alt={event.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${catColor}20` }}>
                  <CatIcon className="w-5 h-5" style={{ color: catColor }} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-sm truncate">{event.name}</h4>
                  {isHost && <Badge className="bg-gray-100 text-gray-700 text-[10px]">Host</Badge>}
                </div>
                <p className="text-xs text-gray-500">
                  {event.date ? (() => {
                    const [year, month, day] = event.date.split('T')[0].split('-').map(Number);
                    const dateObj = new Date(year, month - 1, day);
                    let dateStr = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    if (event.time) {
                      const [hours, mins] = event.time.split(':').map(Number);
                      const ampm = hours >= 12 ? 'PM' : 'AM';
                      const hr12 = hours % 12 || 12;
                      dateStr += ` - ${hr12}:${String(mins).padStart(2, '0')} ${ampm}`;
                    }
                    return dateStr;
                  })() : event.location || "View details"}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => onViewEvent(event)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        );
      })}

      {/* Empty state: occasion suggestions from admin */}
      {!hasContent && !loadingAppts && (
        <div className="space-y-2">
          {[
            { name: "Plan a Service", desc: "Browse services and book with a provider", icon: CATEGORY_PRESETS.makeup.icon, color: CATEGORY_PRESETS.makeup.iconColor },
            { name: "Plan around an Occasion", desc: "Create preferences for an upcoming occasion", icon: Calendar, color: "#374151" }
          ].map((suggestion, idx) => (
            <Card key={idx} className="border-gray-200 border-dashed cursor-pointer hover:border-gray-300 transition-colors" onClick={() => navigate('/experiences')}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${suggestion.color}15` }}>
                  <suggestion.icon className="w-5 h-5" style={{ color: suggestion.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm">{suggestion.name}</h4>
                  <p className="text-xs text-gray-500">{suggestion.desc}</p>
                </div>
                <Plus className="w-4 h-4 text-gray-400" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export const HostingSection = ({ hostedEvents = [], organizations = [], onManageEvent, onManageOrg, onDeleteEvent }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  // Iter 9.47.j - Hosting section title is sourced from Airtable `surface_me`.
  const { slotMap: meSlots } = useUiSurface("surface_me");
  const hostingSectionTitle = slotText(meSlots, "hosting_section_title", "Hosting");
  const [eventCounts, setEventCounts] = useState({});
  // Iter 9.47.p - also count hosted_brands so the section renders for users
  // whose ONLY hosting state is a provider-waitlist signup (no events / orgs yet).
  const [hostedBrandsCount, setHostedBrandsCount] = useState(0);
  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (!token) return;
    fetch(`${API}/me/hosted-brands`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : { hosted_brands: [] })
      .then((d) => setHostedBrandsCount((d.hosted_brands || []).length))
      .catch(() => setHostedBrandsCount(0));
  }, [API]);
  const [hostingData, setHostingData] = useState(null);
  const [loadingHosting, setLoadingHosting] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState(new Set());
  // Iter 9.30 - new modal replaces the chain of native window.prompt() calls
  // when host clicks "Add another dashboard".
  const [addDashboardOrg, setAddDashboardOrg] = useState(null);
  const hasLoadedOnce = useRef(false);
  
  // Helper to get correct image URL
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http') || path.startsWith('data:')) return path;
    return `${BASE_URL}${path}`;
  };
  
  // Toggle org expansion
  const toggleOrg = (orgId) => {
    setExpandedOrgs(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };
  
  // Fetch hosting data (instructor pilots + event pilots)
  useEffect(() => {
    // If already loaded once, skip showing loading spinner on re-mount
    if (hasLoadedOnce.current) {
      setLoadingHosting(false);
      return;
    }
    const fetchHostingData = async () => {
      const token = localStorage.getItem('session_token');
      if (!token) {
        setLoadingHosting(false);
        return;
      }
      
      try {
        const res = await fetch(`${API}/me/hosting`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setHostingData(data);
          // Auto-expand all orgs on first load
          const orgIds = new Set([
            ...(data.organizations || []).map(o => o.org_id),
            ...(data.instructor_pilots || []).map(p => p.org?.org_id).filter(Boolean)
          ]);
          setExpandedOrgs(orgIds);
        }
      } catch (err) {
        console.error("Failed to fetch hosting data:", err);
      }
      hasLoadedOnce.current = true;
      setLoadingHosting(false);
    };
    
    fetchHostingData();
  }, [API]);
  
  // Fetch real-time RSVP counts for hosted events
  // Use refs to prevent infinite loops
  const hasFetchedCounts = useRef(false);
  const eventsToCountRef = useRef([]);
  
  useEffect(() => {
    const eventsToCount = hostingData?.event_pilots || hostedEvents;
    
    // Only fetch if we have events and haven't already fetched
    if (eventsToCount.length === 0) return;
    
    // Check if the event list has actually changed
    const eventIds = eventsToCount.map(e => e.event_id).sort().join(',');
    const prevEventIds = eventsToCountRef.current.map(e => e.event_id).sort().join(',');
    
    if (eventIds === prevEventIds && hasFetchedCounts.current) return;
    
    eventsToCountRef.current = eventsToCount;
    hasFetchedCounts.current = true;
    
    const fetchCounts = async () => {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      
      const counts = {};
      for (const event of eventsToCount) {
        // Prefer the synced client_count from /me/host-events when present
        // (counts provider_clients UNION rsvps deduped by email).
        if (typeof event.client_count === "number") {
          counts[event.event_id] = event.client_count;
          continue;
        }
        try {
          const res = await fetch(`${API}/events/${event.event_id}/rsvps`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            counts[event.event_id] = data.rsvps?.length || 0;
          }
        } catch (err) {
          counts[event.event_id] = event.client_count ?? event.rsvp_count ?? 0;
        }
      }
      setEventCounts(counts);
    };
    
    fetchCounts();
    // Removed 30s polling per user request. Counts refresh on mount + when event_pilots changes (or via manual refresh).
  }, [hostingData?.event_pilots, API]);
  
  // Use hostingData if available, otherwise fall back to props
  const instructorPilots = hostingData?.instructor_pilots || [];
  const eventPilots = hostingData?.event_pilots || hostedEvents;
  const orgs = hostingData?.organizations || organizations;
  
  // Group everything by organization
  const orgGroups = {};
  
  // Add orgs from the organizations list
  orgs.forEach(org => {
    if (!orgGroups[org.org_id]) {
      orgGroups[org.org_id] = {
        ...org,
        pilots: [],
        events: []
      };
    }
  });
  
  // Add instructor pilots to their orgs
  instructorPilots.forEach(pilot => {
    const orgId = pilot.org?.org_id || pilot.org_id;
    if (orgId) {
      if (!orgGroups[orgId]) {
        orgGroups[orgId] = {
          org_id: orgId,
          name: pilot.org?.name || 'Unknown Org',
          pilots: [],
          events: []
        };
      }
      orgGroups[orgId].pilots.push(pilot);
    }
  });
  
  // Add events to their orgs - match by org_id, org_name, or host_orgs
  eventPilots.forEach(event => {
    let targetOrgId = event.org_id;
    
    // If org_id doesn't match any known org, try to find by org_name or host_orgs
    if (!orgGroups[targetOrgId]) {
      const orgName = event.org_name || event.host_orgs?.[0]?.name;
      if (orgName) {
        // Find org by name
        const matchingOrg = Object.values(orgGroups).find(o => 
          o.name?.toLowerCase() === orgName.toLowerCase()
        );
        if (matchingOrg) {
          targetOrgId = matchingOrg.org_id;
        }
      }
    }
    
    // If still no match, create org from event's host_orgs data
    if (!orgGroups[targetOrgId] && (event.org_name || event.host_orgs?.[0]?.name)) {
      const orgName = event.org_name || event.host_orgs?.[0]?.name;
      orgGroups[targetOrgId] = {
        org_id: targetOrgId,
        name: orgName,
        pilots: [],
        events: []
      };
    }
    
    if (targetOrgId && orgGroups[targetOrgId]) {
      orgGroups[targetOrgId].events.push(event);
    }
  });
  
  // Get orphan events (not linked to any org)
  const orphanEvents = eventPilots.filter(e => {
    const orgId = e.org_id;
    const orgName = e.org_name || e.host_orgs?.[0]?.name;
    // Check if event was added to any org
    return !Object.values(orgGroups).some(org => 
      org.events.some(ev => ev.event_id === e.event_id)
    );
  });
  
  // Show ALL orgs (even if empty) so user can create events under them
  // Only filter out if org has no pilots AND no events AND wasn't in the original orgs list
  const orgList = Object.values(orgGroups).filter(org => 
    org.pilots.length > 0 || org.events.length > 0 || orgs.some(o => o.org_id === org.org_id)
  );
  
  if (loadingHosting) {
    return (
      <Card className="border-gray-200 bg-gray-50/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-gray-500">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading hosting...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Removed PortalMembershipsSection per user feedback - concept rejected
  const hasHostingContent = orgList.length > 0 || orphanEvents.length > 0 || hostedBrandsCount > 0;
  
  // Empty state: surface a Join Phygital CTA so consumers know they can apply to host.
  if (!hasHostingContent) {
    // Iter 9.29: Hosting section is BLANK by default until user submits a waitlist
    // or subscribe intake. The "Want to host on Phygital?" CTA was removed from /me
    // per product direction - host onboarding is initiated from the unified
    // EarlyAccessModal opened from sales pages or the Settings-tab "Become a Provider"
    // card (only shown to non-hosts).
    return null;
  }
  
  return (
    <>
    <div className="space-y-3">
      {/* Hosting section - Only for Hosts with orgs/events */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-gray-700 flex items-center gap-2" data-testid="me-hosting-title">
          <Users className="w-4 h-4" />
          {hostingSectionTitle}
        </h3>
      </div>

      {/* Iter 9.47.p - Hosted-brands status cards (provider waitlist + intake +
          live-dashboard states). Sits ABOVE the legacy hosted-events / orgs
          rows so it's the first thing users see when they have a pending
          host application. */}
      <HostedBrandsSection />
          
          {/* Organizations with their pilots and events */}
          {orgList.map((org) => {
        const isExpanded = expandedOrgs.has(org.org_id);
        const totalItems = org.pilots.length + org.events.length;
        
        return (
          <Card key={org.org_id} className="border-indigo-200 bg-indigo-50/30 overflow-hidden">
            <CardContent className="p-0">
              {/* Org Header - Clickable to expand/collapse */}
              <div 
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-indigo-100/50 transition-colors"
                onClick={() => toggleOrg(org.org_id)}
              >
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  {org.logo_url || org.logo ? (
                    <img 
                      src={getImageUrl(org.logo_url || org.logo)} 
                      alt={org.name} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <DefaultOrgIcon orgName={org.name} size={48} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm">{org.name}</h4>
                  <p className="text-xs text-gray-500">
                    {org.pilots.length > 0 && `${org.pilots.length} pilot${org.pilots.length !== 1 ? 's' : ''}`}
                    {org.pilots.length > 0 && org.events.length > 0 && ' • '}
                    {org.events.length > 0 && `${org.events.length} dashboard${org.events.length !== 1 ? 's' : ''}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {(() => {
                    // Iter 9.48.3.j - Connect Acuity at org row level.
                    // Iter 9.49+ - expand "connected" detection to all the
                    // places the OAuth callback writes to (settings.acuity_*,
                    // tech_stack.acuity.*, root acuity_scheduling_url) so the
                    // button hides immediately after Mia/Shelsea connect.
                    const mainDash = org.events.find(e => !e.parent_event_id && !e.linked_provider_event_id) || org.events[0];
                    const isEventConnected = (ev) => !!(
                      ev?.acuity_connected ||
                      ev?.acuity_scheduling_url ||
                      ev?.tech_stack?.acuity?.status === "connected" ||
                      ev?.tech_stack?.acuity?.scheduling_url ||
                      ev?.settings?.acuity_connected ||
                      ev?.settings?.acuity_access_token
                    );
                    const acuityConnected = !!(
                      org.acuity_connected ||
                      isEventConnected(mainDash) ||
                      (org.events || []).some(isEventConnected)
                    );
                    if (!mainDash) return null;
                    return acuityConnected ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200"
                        data-testid={`org-${org.org_id}-acuity-connected-badge`}
                      >
                        <Check className="w-3 h-3" />
                        Acuity
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-stone-300 text-stone-700 hover:bg-stone-50"
                        data-testid={`org-${org.org_id}-connect-acuity-btn`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const resp = await fetch(`${API}/acuity/auth-url?event_id=${mainDash.event_id}`);
                            const data = await resp.json();
                            if (data.auth_url) {
                              window.location.href = data.auth_url;
                            }
                          } catch (err) {
                            console.error("Acuity connect err:", err);
                          }
                        }}
                      >
                        Connect Acuity
                      </Button>
                    );
                  })()}
                  {onManageOrg && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-indigo-300 text-indigo-700"
                      onClick={(e) => { e.stopPropagation(); onManageOrg(org); }}
                    >
                      Settings
                    </Button>
                  )}
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </div>
              
              {/* Expanded Content - Pilots and Events */}
              {isExpanded && (
                <div className="border-t border-indigo-200 bg-white/50">
                  {/* Instructor Pilots under this org */}
                  {org.pilots.map((pilot) => {
                    const pilotPreset = resolveCategory(pilot.pilot_type || pilot.category);
                    const PilotIcon = pilotPreset?.icon || GraduationCap;
                    const pilotBg = pilot.color || pilotPreset?.iconColor || '#8b5cf6';
                    return (
                    <div key={pilot.profile_id} className="p-3 border-b border-indigo-100 last:border-b-0">
                      <div className="flex items-center gap-3 ml-2">
                        <div className="w-2 h-2 rounded-full bg-violet-400" />
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: pilotBg }}
                        >
                          <PilotIcon className="w-4 h-4" style={{ color: '#ffffff' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-sm">{pilot.name}</h5>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="outline" className="text-[10px] bg-violet-100 border-violet-200 text-violet-700">
                              {pilot.pilot_type || 'Instructor'}
                            </Badge>
                            {pilot.default_event_type && (
                              <Badge variant="outline" className="text-[10px] bg-gray-100">
                                {pilot.default_event_type === 'url' ? 'Virtual' : pilot.default_event_type === 'irl' ? 'IRL' : 'Hybrid'}
                              </Badge>
                            )}
                            <span className="text-[10px] text-gray-500">{pilot.event_count || 0} events</span>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="h-7 text-xs text-violet-600"
                          onClick={() => window.location.href = `/admin/instructor-pilots/${pilot.profile_id}`}
                          data-testid={`manage-pilot-${pilot.profile_id}`}
                        >
                          Manage
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                  
                  {/* Events under this org - Iter 9.36: nest add-on dashboards (instructor/event) under their parent */}
                  {(() => {
                    const all = org.events || [];
                    const childByParent = {};
                    const topLevel = [];
                    all.forEach(ev => {
                      const parentId = ev.linked_provider_event_id || ev.parent_event_id;
                      if (parentId && all.some(p => p.event_id === parentId)) {
                        (childByParent[parentId] ||= []).push(ev);
                      } else {
                        topLevel.push(ev);
                      }
                    });
                    return topLevel.map(event => ({ event, children: childByParent[event.event_id] || [] }));
                  })().map(({ event, children }) => {
                    const guestCount = eventCounts[event.event_id] ?? event.client_count ?? event.rsvp_count ?? 0;
                    const catColor = getCatColor(event.pilot_category, '#6366F1');
                    const CatIcon = getCatIcon(event.pilot_category, PartyPopper);
                    const stage = event.pilot_stage || (event.dashboard_status === "active" ? "active" : "submitted");
                    const stageCopy = PILOT_STAGE_COPY[stage];
                    const isActive = stage === "active";
                    return (
                      <div key={event.event_id} className="border-b border-indigo-100 last:border-b-0" data-testid={`hosting-tile-${event.event_id}`}>
                        <div className="p-3">
                          <div className="flex items-center gap-3 ml-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stageCopy?.badge_color || catColor }} />
                          {/* /me Hosting tile - light category bg, vector icon (R11) - icon sized 50% of tile per spec */}
                          <div className="w-10 h-10 rounded-lg overflow-hidden shadow-sm flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: getCatBg(event.pilot_category, '#F3F4F6') }}>
                            {event.cover_image ? (
                              <img src={getImageUrl(event.cover_image)} alt={event.name} className="w-full h-full object-cover" data-testid={`event-cover-${event.event_id}`} />
                            ) : event.logo_image ? (
                              <img src={getImageUrl(event.logo_image)} alt={event.name} className="w-full h-full object-contain" data-testid={`event-logo-${event.event_id}`} />
                            ) : event.category_logo_url ? (
                              /* Iter 9.47.t - fall back to category logo before vector icon */
                              <img src={event.category_logo_url} alt={event.name} className="w-full h-full object-contain p-1" data-testid={`event-cat-logo-${event.event_id}`} />
                            ) : (
                              <CatIcon className="w-7 h-7" style={{ color: catColor }} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium truncate">{event.name}</p>
                              {event.is_demo && <span className="px-1 py-0.5 text-[7px] font-bold bg-amber-100 text-amber-700 rounded uppercase flex-shrink-0">demo</span>}
                              {!isActive && stageCopy && (
                                <span className="px-1.5 py-0.5 text-[8px] font-semibold rounded-full" style={{ backgroundColor: `${stageCopy.badge_color}1a`, color: stageCopy.badge_color }} data-testid={`stage-badge-${stage}`}>
                                  {stageCopy.label}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {isActive ? (
                                <span className="text-[10px] text-gray-500" data-testid={`client-count-${event.event_id}`}>{guestCount} client{guestCount !== 1 ? 's' : ''}</span>
                              ) : (
                                <span className="text-[10px] text-gray-600" data-testid={`stage-body-${event.event_id}`}>{stageCopy?.body}</span>
                              )}
                              {event.pilot_category && (
                                <Badge variant="outline" className="text-[8px] px-1 py-0 h-3 capitalize">
                                  {event.pilot_category}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {isActive && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-7 text-xs"
                                style={{ color: catColor }}
                                onClick={() => onManageEvent(event)}
                                data-testid="manage-event-btn"
                              >
                                Manage
                              </Button>
                            )}
                            {!isActive && stageCopy?.is_clickable && stageCopy?.cta_label && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                style={{ color: stageCopy.badge_color }}
                                onClick={() => { window.location.href = stageCopy.cta_to(event); }}
                                data-testid={`stage-cta-${stage}`}
                              >
                                {stageCopy.cta_label}
                              </Button>
                            )}
                          </div>
                          </div>
                        </div>

                        {/* Iter 9.36 - child sub-dashboards (instructor / event addons) nested under parent */}
                        {children.length > 0 && (
                          <div className="ml-12 pl-4 border-l-2 border-purple-100 mb-2 space-y-1">
                            {children.map(child => {
                              const childCount = eventCounts[child.event_id] ?? child.client_count ?? child.rsvp_count ?? 0;
                              const isInstructor = child.is_instructor || child.dashboard_kind === "instructor";
                              // Iter 9.49+ R6 - expand category fallback chain so child
                              // sub-dashboards never fall through to the blue PartyPopper.
                              // Children inherit pilot_category from parent / org; if those
                              // are also missing, use `category` (legacy field). Instructor
                              // add-ons always override to "instructor".
                              const childCatKey = isInstructor
                                ? "instructor"
                                : (child.pilot_category || child.category || org?.pilot_category || org?.category);
                              const childCatColor = getCatColor(childCatKey, '#6366F1');
                              const ChildIcon = getCatIcon(childCatKey, isInstructor ? GraduationCap : PartyPopper);
                              const childActive = (child.pilot_stage || child.dashboard_status) === "active" || child.dashboard_status === "active";
                              // Iter 9.47.u - "Event add-on" violates vocabulary ban.
                              // host_brand subtype gets its own label; everything else is "Experience add-on".
                              const isHostBrand = child.subtype === "host_brand";
                              const subLabel = isInstructor
                                ? "Instructor add-on"
                                : isHostBrand
                                  ? "Brand showcase"
                                  : "Experience add-on";
                              const clientNoun = isInstructor ? "learner" : "guest";
                              return (
                                <div key={child.event_id} className="flex items-center gap-2.5 py-2 pr-2" data-testid={`hosting-subtile-${child.event_id}`}>
                                  <span className="text-purple-300 text-xs">└</span>
                                  <div className="w-7 h-7 rounded-md overflow-hidden flex items-center justify-center" style={{ backgroundColor: getCatBg(childCatKey, '#F3F4F6') }}>
                                    <ChildIcon className="w-4 h-4" style={{ color: childCatColor }} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-xs font-medium truncate">{child.name}</p>
                                      <span className="px-1.5 py-0.5 text-[8px] font-medium rounded-full bg-purple-100 text-purple-700">
                                        {subLabel}
                                      </span>
                                    </div>
                                    <span className="text-[10px] text-gray-500" data-testid={`subdash-count-${child.event_id}`}>
                                      {childCount} {clientNoun}{childCount !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  {childActive && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 text-[11px] px-2"
                                      style={{ color: childCatColor }}
                                      onClick={() => onManageEvent(child)}
                                      data-testid={`manage-subdash-${child.event_id}`}
                                    >
                                      Manage
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Add another dashboard - admin-gated approval (Iter 9.30 modal).
                      Replaces the chain of window.prompt() calls. */}
                  <div className="p-3 border-t border-indigo-100">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                      onClick={() => setAddDashboardOrg(org)}
                      data-testid={`add-dashboard-${org.org_id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add another dashboard
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
      
      {/* Orphan Events - not linked to any org */}
      {orphanEvents.map((event) => {
        const guestCount = eventCounts[event.event_id] ?? event.client_count ?? event.rsvp_count ?? 0;
        const catColor = getCatColor(event.pilot_category, '#6366F1');
        const CatIcon = getCatIcon(event.pilot_category, PartyPopper);
        return (
          <Card key={event.event_id} className="border-gray-200 bg-gray-50/30">
            <CardContent className="p-3 flex items-center gap-3">
              {/* /me Hosting orphan tile - light category bg, vector icon (R11) - 50% sized */}
              <div className="w-12 h-12 rounded-xl overflow-hidden shadow-sm flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: getCatBg(event.pilot_category, '#F3F4F6') }}>
                {event.cover_image ? (
                  <img 
                    src={getImageUrl(event.cover_image)} 
                    alt={event.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <CatIcon className="w-7 h-7" style={{ color: catColor }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm">{event.name}</h4>
                <p className="text-xs text-gray-500" data-testid={`client-count-orphan-${event.event_id}`}>
                  {guestCount} client{guestCount !== 1 ? 's' : ''}
                </p>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                className="border-gray-300 text-gray-700"
                onClick={() => onManageEvent(event)}
                data-testid="manage-event-btn"
              >
                Manage
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
    {/* Iter 9.30 - modal-based "Add another dashboard" flow (replaces native prompts) */}
    <AddDashboardModal
      open={!!addDashboardOrg}
      onOpenChange={(o) => !o && setAddDashboardOrg(null)}
      org={addDashboardOrg}
    />
    </>
  );
};


// Status badge configuration
const PORTAL_STATUS_CONFIG = {
  draft: {
    label: 'Coming Soon',
    color: 'bg-gray-100 text-gray-600 border-gray-300',
    icon: Clock,
    canAccess: false
  },
  testing: {
    label: 'Pilot Testing',
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    icon: TestTube,
    canAccess: true
  },
  live: {
    label: 'Live',
    color: 'bg-green-100 text-green-700 border-green-300',
    icon: Rocket,
    canAccess: true
  }
};


// PortalMembershipsSection removed - concept rejected per user feedback
// Host/Participant distinction is handled in the HostingSection

export const ProfileCompletionCard = ({ 
  items = [],
  onAction,
  onDismiss 
}) => {
  if (items.length === 0) return null;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm text-gray-600">Complete Profile</h3>
        <span className="text-xs text-gray-400">{items.length} left</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.id} className="relative flex-shrink-0">
              <button
                onClick={() => onAction(item)}
                className="flex flex-col items-center gap-1.5 p-3 bg-white rounded-xl border border-gray-100 shadow-sm active:scale-[0.98] transition-all min-w-[80px]"
              >
                <div className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center`}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-[11px] font-medium">{item.title}</span>
              </button>
              {item.dismissible && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDismiss(item.id); }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center"
                >
                  <X className="w-3 h-3 text-gray-600" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WalletCardSettings;
